import { css, html } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import type { EmployeeForStaffingCalendar } from 'src/store/selectors';
import { defaultWorkEndTime, defaultWorkStartTime, defaultWorkWeek } from 'src/store/selectors';
import { LocalDate } from 'src/utilities/local-date.js';

import '../../library/editors/components/d-select-time.js';
import '../../library/editors/elements/d-checkbox.js';
import '../../library/editors/elements/d-select-date.js';
import '../../library/elements/d-action.js';
import '../../library/elements/d-label.js';
import '../../library/elements/d-section.js';
import '../../library/elements/d-smooth-resize.js';
import '../../library/fields/d-view-info.js';

import './d-edit-work-schedule.js';
import { cloneDeep } from 'lodash';
import { BaseDialog } from 'src/library/components/BaseDialog.js';
import { produce } from 'immer';
import type { WorkSchedule } from '@trinnvis/dabih-api-client';
import { WorkScheduleRestPeriodEnum } from '@trinnvis/dabih-api-client';
import type { EditWorkSchedulesInput } from './edit-work-schedules-input';
import type { EditWorkSchedulesResult } from './edit-work-schedules-result';
import type { DialogActionInput } from 'src/library/components/DialogActionInput';
import { t } from 'src/i18n';

/**
 *
 * USAGE:
 *    d-employee-view
 *    d-staffing-calendar-table
 *
 */
@customElement('edit-work-schedules-dialog')
export class EditWorkSchedulesDialog extends BaseDialog<EditWorkSchedulesInput, EditWorkSchedulesResult> {
  static readonly styles = BaseDialog.styles.concat(css`
    d-wrap {
      max-width: 100%;
      box-sizing: border-box;
    }
  `);
  @query('#schedules')
  schedulesElement!: HTMLElement;
  @property({ type: Object })
  employee?: EmployeeForStaffingCalendar;
  @property({ type: Array })
  schedules: WorkSchedule[] = [];
  @property({ type: String })
  day = '';
  @property({ type: Boolean })
  show = false;
  width = 720;

  protected get calculatedTitle() {
    return t('staffing.editWorkPatternTitle');
  }

  protected get calculatedSubtitle(): string {
    return this.employee && this.employee.name
      ? t('staffing.editWorkPatternSubtitleFor', { name: this.employee.name })
      : '';
  }

  protected get calculatedFooterActions(): DialogActionInput[] {
    return [
      { name: t('common:cancel'), action: 'cancel' },
      { name: t('staffing.done'), action: 'done', main: true },
    ];
  }

  private get schedulesForEditor() {
    return this.schedules.map((s) => {
      return {
        start: s.start,
        holidays: s.holidaysAreTimeOff,
        workHoursType: s.workHoursDefinedPerDay ? 'custom' : 'default',
        restPeriod: s.restPeriod,
        workDayStart: s.workDayStartTime,
        workDayEnd: s.workDayEndTime,
        workWeeks: s.workWeeks.map((week) => {
          return [week.monday, week.tuesday, week.wednesday, week.thursday, week.friday, week.saturday, week.sunday];
        }),
      };
    });
  }

  private get nextAvailableDate() {
    const scheduleStarts = this.schedules.map((s) => {
      return LocalDate.fromString(s.start).toString();
    });
    let start = LocalDate.fromString(this.day).toString();
    while (scheduleStarts.includes(start)) {
      start = LocalDate.fromString(start).plusDays(1).toString();
    }
    return start;
  }

  _new() {
    this.schedules = [
      ...this.schedules,
      {
        start: this.nextAvailableDate,
        holidaysAreTimeOff: true,
        workHoursDefinedPerDay: false,
        restPeriod: WorkScheduleRestPeriodEnum.WithoutPay,
        workDayStartTime: defaultWorkStartTime,
        workDayEndTime: defaultWorkEndTime,
        workWeeks: [defaultWorkWeek],
      },
    ];
  }

  renderBody() {
    return html`
      <d-section>
        <d-view-info info-list field="staffing_editWorkHours"></d-view-info>
      </d-section>
      <d-smooth-resize>
        ${this.schedules.map(
          (schedule, index) => html`
            <d-section>
              <d-wrap split-reverse>
                <d-action delete plain @click=${() => this.onScheduleDelete(index)}
                  >${t('staffing.deletePeriod')}</d-action
                >
                <d-edit-work-schedule
                  .employee=${this.employee}
                  .schedule=${schedule}
                  .schedules=${this.schedules}
                  @schedule-changed=${(e) => this.onScheduleChanged(e, index)}
                ></d-edit-work-schedule>
              </d-wrap>
            </d-section>
          `,
        )}
      </d-smooth-resize>
      <d-section>
        <d-wrap right>
          <d-action plain @click=${() => this._new()}>${t('staffing.newPeriod')}</d-action>
        </d-wrap>
      </d-section>
    `;
  }

  protected fetchResult(detail: string | undefined): EditWorkSchedulesResult {
    if (detail === 'done') {
      return {
        action: 'done',
        workSchedules: this.schedules,
      };
    }
    return {
      action: 'cancel',
    };
  }

  protected initializeDialog(input: EditWorkSchedulesInput) {
    this.employee = input.employee;
    this.day = input.defaultDate;
    this.schedules = cloneDeep(input.employee?.workSchedules ?? []);
  }

  private onScheduleDelete(index: number) {
    this.schedules = produce(this.schedules, (draft) => {
      draft.splice(index, 1);
    });
  }

  private onScheduleChanged(e: CustomEvent<{ schedule: WorkSchedule }>, index: number) {
    e.stopPropagation();
    this.schedules = produce(this.schedules, (draft) => {
      draft[index] = e.detail.schedule;
    });
    this.fireSchedulesChanged();
  }

  private fireSchedulesChanged() {
    this.dispatchEvent(
      new CustomEvent<{ schedules: WorkSchedule[] }>('schedules-changed', {
        composed: true,
        bubbles: true,
        detail: { schedules: this.schedules },
      }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'edit-work-schedules-dialog': EditWorkSchedulesDialog;
  }
}
